import { CartAddEvent } from '@theme/events';
import { compactEaLineProperties } from '@theme/ea-cart';
import { getActiveCartDrawer } from '@theme/utilities';

const DIALOG_ID = 'size-quick-add-dialog';
const MOBILE_MEDIA = typeof window !== 'undefined' ? window.matchMedia('(max-width: 749px)') : null;
const SELECTOR_TITLE = '.size-quick-add-modal__title';
const SELECTOR_PRICE = '.size-quick-add-modal__price';
const SELECTOR_SIZES = '[data-size-quick-add-sizes]';
const SELECTOR_LOADING = '[data-size-quick-add-loading]';
const SELECTOR_CLOSE = '[data-size-quick-add-close]';
const SELECTOR_MEDIA = '[data-size-quick-add-media]';
const SELECTOR_IMAGE = '[data-size-quick-add-image]';
const SELECTOR_COLORS = '[data-size-quick-add-colors]';
const SELECTOR_COLORS_ROW = '[data-size-quick-add-colors-row]';
const SELECTOR_COLOR_NAME = '[data-size-quick-add-color-name]';
const SELECTOR_COLORS_COUNT = '[data-size-quick-add-colors-count]';
const SELECTOR_SIZES_LABEL = '[data-size-quick-add-sizes-label]';
const SELECTOR_NOTIFY = '[data-size-quick-add-notify]';

function isMobile() {
  return MOBILE_MEDIA?.matches ?? false;
}

function getSizeOption(options) {
  if (!options?.length) return null;
  const sizeOpt = options.find((o) => String(o.name).toLowerCase().includes('size'));
  return sizeOpt || options[0] || null;
}

function getVariantForValue(variants, value, optionPosition = 1) {
  const key = `option${optionPosition}`;
  const v = variants.find((v) => v[key] === value);
  return v ? { id: v.id, available: v.available } : null;
}

/**
 * Format price with shop's money format (currency symbol and/or code).
 * @param {number} cents - Price in cents
 * @param {string} [moneyFormat] - e.g. "${{amount}}" or "{{amount}} AUD"
 * @param {string} [currency] - e.g. "AUD", "USD"
 * @returns {string}
 */
function formatPrice(cents, moneyFormat, currency) {
  const amount = (Number(cents) / 100).toFixed(2);
  const amountNoDecimals = Math.round(Number(cents) / 100).toString();
  if (moneyFormat && typeof moneyFormat === 'string') {
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/gi, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, amountNoDecimals)
      .replace(/\{\{\s*currency\s*\}\}/gi, currency || '')
      .trim();
  }
  if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(cents);
  }
  return amount;
}

function fetchProduct(handle) {
  const root = (typeof window.Shopify !== 'undefined' && window.Shopify.routes?.root) || '/';
  return fetch(`${root}products/${handle}.js`).then((r) => r.json());
}

function init() {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(DIALOG_ID));
  if (!dialog) return;

  const titleEl = dialog.querySelector(SELECTOR_TITLE);
  const priceEl = dialog.querySelector(SELECTOR_PRICE);
  const sizesEl = dialog.querySelector(SELECTOR_SIZES);
  const loadingEl = dialog.querySelector(SELECTOR_LOADING);
  const closeBtn = dialog.querySelector(SELECTOR_CLOSE);
  const mediaEl = /** @type {HTMLElement | null} */ (dialog.querySelector(SELECTOR_MEDIA));
  const imageEl = /** @type {HTMLImageElement | null} */ (dialog.querySelector(SELECTOR_IMAGE));
  const colorsEl = dialog.querySelector(SELECTOR_COLORS);
  const colorsRowEl = /** @type {HTMLElement | null} */ (dialog.querySelector(SELECTOR_COLORS_ROW));
  const colorNameEl = dialog.querySelector(SELECTOR_COLOR_NAME);
  const colorsCountEl = /** @type {HTMLElement | null} */ (dialog.querySelector(SELECTOR_COLORS_COUNT));
  const sizesLabelEl = /** @type {HTMLElement | null} */ (dialog.querySelector(SELECTOR_SIZES_LABEL));
  const notifyEl = /** @type {HTMLAnchorElement | null} */ (dialog.querySelector(SELECTOR_NOTIFY));

  if (!sizesEl) return;

  let currentHandle = null;
  /** @type {Array<{handle:string,name:string,image:string,image2x:string,url:string,available?:boolean,swatchBg:string}>} */
  let colorsList = [];
  /** @type {Record<string, string>} */
  let pendingEaLineProperties = {};

  function close() {
    try {
      dialog.close();
    } catch (_) {}
  }

  function showLoading(show) {
    if (loadingEl) loadingEl.hidden = !show;
    if (sizesEl) sizesEl.hidden = show;
    if (show && sizesLabelEl) sizesLabelEl.hidden = true;
    if (show && notifyEl) notifyEl.hidden = true;
    if (titleEl) titleEl.textContent = '';
    if (priceEl) priceEl.textContent = '';
  }

  function renderSizes(product) {
    const options = product.options || [];
    const sizeOption = getSizeOption(options);
    const variants = product.variants || [];

    sizesEl.innerHTML = '';
    if (sizesLabelEl) sizesLabelEl.hidden = true;
    if (!sizeOption) return;

    const optionPosition = sizeOption.position != null ? Number(sizeOption.position) : 1;
    const values = sizeOption.values || [];

    values.forEach((value) => {
      const variant = getVariantForValue(variants, value, optionPosition);
      const available = variant ? variant.available : false;
      const variantId = variant ? variant.id : null;

      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'size-quick-add-modal__size-btn';
      btn.disabled = !available;
      if (variantId) btn.dataset.variantId = String(variantId);
      btn.setAttribute('aria-label', available ? value : `${value} – Sold out`);
      if (!available) btn.setAttribute('aria-disabled', 'true');

      const label = document.createElement('span');
      label.className = 'size-quick-add-modal__size-btn-label';
      label.textContent = value;
      btn.append(label);

      const spinner = document.createElement('span');
      spinner.className = 'size-quick-add-modal__size-btn-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      spinner.innerHTML =
        '<svg class="size-quick-add-modal__size-btn-spinner-svg" width="14" height="14" viewBox="0 0 66 66" fill="none" focusable="false"><circle class="size-quick-add-modal__size-btn-spinner-path" cx="33" cy="33" r="30" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>';
      btn.append(spinner);

      btn.addEventListener('click', async () => {
        const id = btn.dataset.variantId;
        if (!id || btn.disabled) return;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('size-quick-add-modal__size-btn--loading');
        try {
          const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
            .map((el) => el.dataset?.sectionId)
            .filter(Boolean);
          const eaProps = compactEaLineProperties(pendingEaLineProperties);
          const body = {
            items: [
              {
                id: Number(id),
                quantity: 1,
                ...(Object.keys(eaProps).length > 0 ? { properties: eaProps } : {}),
              },
            ],
            ...(sectionIds.length > 0 && { sections: sectionIds.join(',') }),
          };
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (data.status && data.status !== 200) throw new Error(data.description || data.message);

          document.dispatchEvent(
            new CartAddEvent({}, 'size-quick-add-modal', {
              source: 'size-quick-add-modal',
              itemCount: 1,
              variantId: id,
              sections: data?.sections,
            })
          );
          document.dispatchEvent(
            new CustomEvent('sabo:quick-shop-add', {
              bubbles: true,
              detail: { handle: currentHandle || '', source: 'modal', size: value },
            })
          );
          close();
          const cartDrawer = getActiveCartDrawer();
          if (cartDrawer?.hasAttribute('auto-open')) {
            setTimeout(() => {
              if (typeof cartDrawer.open === 'function') cartDrawer.open();
            }, 50);
          } else if (typeof cartDrawer?.open === 'function') {
            cartDrawer.open();
          }
        } catch (err) {
          document.dispatchEvent(
            new CartAddEvent({}, 'size-quick-add-modal', { didError: true, source: 'size-quick-add-modal' })
          );
          if (typeof window.Theme !== 'undefined' && window.Theme.translations?.add_to_cart_error) {
            console.warn(window.Theme.translations.add_to_cart_error, err?.message ?? err);
          }
        } finally {
          btn.removeAttribute('aria-busy');
          btn.classList.remove('size-quick-add-modal__size-btn--loading');
        }
      });

      li.append(btn);
      sizesEl.append(li);
    });

    if (sizesLabelEl && values.length) sizesLabelEl.hidden = false;
  }

  /** @param {string} src @param {string} srcset @param {string} alt */
  function setImage(src, srcset, alt) {
    if (!imageEl || !mediaEl) return;
    if (!src) {
      mediaEl.hidden = true;
      return;
    }
    imageEl.src = src;
    if (srcset) imageEl.srcset = srcset;
    else imageEl.removeAttribute('srcset');
    imageEl.alt = alt || '';
    mediaEl.hidden = false;
  }

  /** @param {string} handle */
  function updateColorName(handle) {
    if (!colorNameEl) return;
    const color = colorsList.find((c) => c.handle === handle);
    colorNameEl.textContent = color?.name || '';
  }

  /** Renders the in-modal colour switcher (hidden when there's 0/1 colour). @param {string} selectedHandle */
  function renderColors(selectedHandle) {
    if (!colorsEl) return;
    colorsEl.innerHTML = '';
    const list = Array.isArray(colorsList) ? colorsList : [];
    if (list.length <= 1) {
      if (colorsRowEl) colorsRowEl.hidden = true;
      if (colorNameEl) colorNameEl.textContent = '';
      if (colorsCountEl) colorsCountEl.hidden = true;
      return;
    }
    list.forEach((color) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'size-quick-add-modal__color';
      const soldOut = color.available === false;
      if (soldOut) btn.classList.add('size-quick-add-modal__color--sold-out');
      btn.dataset.handle = color.handle;
      btn.title = color.name || '';
      // Sold-out colours stay clickable; only the label announces the state.
      btn.setAttribute('aria-label', soldOut ? `${color.name || ''} \u2013 Sold out` : color.name || '');
      btn.setAttribute('aria-pressed', color.handle === selectedHandle ? 'true' : 'false');
      const sw = document.createElement('span');
      sw.className = 'swatch';
      if (color.swatchBg) sw.style.background = color.swatchBg;
      sw.style.backgroundSize = 'cover';
      sw.style.backgroundPosition = 'center';
      btn.append(sw);
      btn.addEventListener('click', () => selectColor(color.handle));
      li.append(btn);
      colorsEl.append(li);
    });
    if (colorsRowEl) colorsRowEl.hidden = false;
    // "{n} colours" appears on the right of the label row only when there are more than 4.
    if (colorsCountEl) {
      if (list.length > 4) {
        colorsCountEl.textContent = `${list.length} colours`;
        colorsCountEl.hidden = false;
      } else {
        colorsCountEl.hidden = true;
      }
    }
    updateColorName(selectedHandle);
  }

  /** Fills title/price/sizes (and a fallback image) from a fetched product. */
  function fillProduct(product) {
    if (!isMobile() || !product) return;

    const name = String(product.title ?? product.name ?? '').trim();
    const priceCents = Number(product.price ?? product.price_min ?? product.variants?.[0]?.price ?? 0);
    const compareCents = Number(
      product.compare_at_price ?? product.compare_at_price_max ?? product.compare_at_price_min ?? 0
    );
    const moneyFormat = dialog.getAttribute('data-money-format') || '';
    const currency = dialog.getAttribute('data-currency') || '';
    const onSale = compareCents > priceCents && priceCents > 0;
    const priceFormatted = formatPrice(priceCents, moneyFormat, currency);
    let priceHtml;
    if (onSale) {
      const compareFormatted = formatPrice(compareCents, moneyFormat, currency);
      const pct = Math.max(1, Math.round((1 - priceCents / compareCents) * 100));
      priceHtml =
        `<span class="sqa-price__sale">${priceFormatted}</span>` +
        `<s class="sqa-price__compare">${compareFormatted}</s>` +
        `<span class="sqa-price__badge">${pct}% off</span>`;
    } else {
      priceHtml = `<span class="sqa-price__regular">${priceFormatted}</span>`;
    }

    renderSizes(product);
    showLoading(false);

    // Sold-out colour: sizes render disabled (renderSizes already handles per-variant
    // availability), and we surface a "Notify me" link to the product page.
    const soldOut = product.available === false;
    if (notifyEl) {
      if (soldOut) {
        const activeColor = colorsList.find((c) => c.handle === currentHandle);
        notifyEl.href = (activeColor && activeColor.url) || product.url || '#';
        notifyEl.hidden = false;
      } else {
        notifyEl.hidden = true;
      }
    }

    // If we don't already have a colour image, fall back to the product's featured image.
    if (mediaEl && mediaEl.hidden && product.featured_image) {
      setImage(product.featured_image, '', name);
    }

    requestAnimationFrame(() => {
      const titleNode = dialog.querySelector(SELECTOR_TITLE);
      const priceNode = dialog.querySelector(SELECTOR_PRICE);
      if (titleNode) {
        titleNode.textContent = name;
        titleNode.style.cssText = 'display:block;visibility:visible;color:#111;opacity:1;';
      }
      if (priceNode) {
        priceNode.innerHTML = priceHtml;
        priceNode.style.cssText = 'visibility:visible;opacity:1;';
      }
    });
  }

  /** Switches the modal to another colour without leaving the page. @param {string} handle */
  function selectColor(handle) {
    if (!handle || handle === currentHandle) return;
    currentHandle = handle;

    if (colorsEl) {
      colorsEl.querySelectorAll('.size-quick-add-modal__color').forEach((b) => {
        b.setAttribute('aria-pressed', b instanceof HTMLElement && b.dataset.handle === handle ? 'true' : 'false');
      });
    }
    updateColorName(handle);
    const color = colorsList.find((c) => c.handle === handle);
    if (color && color.image) {
      setImage(color.image, color.image2x ? `${color.image} 1x, ${color.image2x} 2x` : '', color.name);
    }
    document.dispatchEvent(
      new CustomEvent('sabo:color-swap', {
        bubbles: true,
        detail: { handle, color: color?.name || '', source: 'modal' },
      })
    );

    // Keep the current sizes/price visible during the quick re-fetch (no loading flash on switch).
    fetchProduct(handle)
      .then((res) => {
        if (currentHandle !== handle) return;
        const product = res && typeof res === 'object' && res.title != null ? res : res && res.product ? res.product : null;
        fillProduct(product);
      })
      .catch(() => {
        if (loadingEl) loadingEl.hidden = false;
        if (sizesEl) sizesEl.hidden = true;
      });
  }

  /** @param {{ handle?: string, colors?: any[] }} detail */
  function openModal(detail) {
    if (!isMobile()) return;
    const handle = detail?.handle;
    if (!handle) return;

    colorsList = Array.isArray(detail?.colors) ? detail.colors : [];
    currentHandle = handle;

    sizesEl.innerHTML = '';
    if (mediaEl) mediaEl.hidden = true;
    renderColors(handle);

    // Instant image from the pre-rendered colour data (before the JSON fetch resolves).
    const color = colorsList.find((c) => c.handle === handle);
    if (color && color.image) {
      setImage(color.image, color.image2x ? `${color.image} 1x, ${color.image2x} 2x` : '', color.name);
    }

    showLoading(true);
    dialog.showModal();

    fetchProduct(handle)
      .then((res) => {
        if (currentHandle !== handle) return;
        const product = res && typeof res === 'object' && res.title != null ? res : res && res.product ? res.product : null;
        fillProduct(product);
      })
      .catch(() => {
        if (loadingEl) loadingEl.hidden = false;
        if (sizesEl) sizesEl.hidden = true;
      });
  }

  document.addEventListener('size-quick-add-modal:open', (/** @type {CustomEvent} */ e) => {
    const raw = e.detail?.eaLineProperties;
    pendingEaLineProperties = raw && typeof raw === 'object' ? { ...raw } : {};
    openModal(e.detail || {});
  });

  if (closeBtn) closeBtn.addEventListener('click', close);
  dialog.addEventListener('cancel', close);
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
}

function runInit() {
  if (document.getElementById(DIALOG_ID)) {
    init();
    return;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInit);
} else {
  runInit();
}
